import { useState } from "react";

import CrisisAlertIcon from "@mui/icons-material/CrisisAlert";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type PlanBlancSeverity,
  declarePlanBlanc,
  endPlanBlanc,
  getPlanBlancState,
} from "@/api/planBlanc";
import { getMyTenant } from "@/api/tenants";
import { usePermission } from "@/auth/usePermission";

/**
 * Compact admin control: shows "Declare Plan Blanc" when none is active, or
 * "End Plan Blanc" when one is. Only renders for users with the permission.
 *
 * Declaring is opt-in per tenant. Ending is not: turning the setting off while
 * a Plan Blanc runs must not strand it with no way to close it.
 */
export function PlanBlancAdminButton({ organizationId }: { organizationId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canDeclare = usePermission("plan_blanc:declare");

  // Shares the cache key with the tenant settings page, so toggling the setting
  // there refreshes this control without a reload.
  const tenant = useQuery({
    queryKey: ["me-tenant"],
    queryFn: getMyTenant,
    enabled: canDeclare,
  });
  const declareAllowed = tenant.data?.plan_blanc_enabled === true;

  const state = useQuery({
    queryKey: ["plan-blanc-state", organizationId],
    queryFn: () => getPlanBlancState(organizationId),
    enabled: canDeclare && Boolean(organizationId),
    refetchInterval: 60_000,
  });

  const [declareOpen, setDeclareOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [severity, setSeverity] = useState<PlanBlancSeverity>("BRAVO");
  const [reason, setReason] = useState("");
  const [endReason, setEndReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const declareMutation = useMutation({
    mutationFn: () => declarePlanBlanc(organizationId, severity, reason || undefined),
    onSuccess: () => {
      setDeclareOpen(false);
      setReason("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["plan-blanc-state", organizationId] });
      qc.invalidateQueries({ queryKey: ["plan-blanc-active"] });
    },
    onError: (e: unknown) => {
      const d = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(d || "error");
    },
  });

  const endMutation = useMutation({
    mutationFn: () => endPlanBlanc(organizationId, endReason || undefined),
    onSuccess: () => {
      setEndOpen(false);
      setEndReason("");
      qc.invalidateQueries({ queryKey: ["plan-blanc-state", organizationId] });
      qc.invalidateQueries({ queryKey: ["plan-blanc-active"] });
    },
  });

  if (!canDeclare) return null;

  const active = state.data?.active;
  // Nothing to show: no crisis to close, and this tenant may not open one.
  if (!active && !declareAllowed) return null;

  return (
    <>
      {active ? (
        <Button
          variant="contained"
          color="success"
          startIcon={<StopCircleIcon />}
          onClick={() => setEndOpen(true)}
        >
          {t("planBlanc.endCta")}
        </Button>
      ) : (
        <Button
          variant="outlined"
          color="error"
          startIcon={<CrisisAlertIcon />}
          onClick={() => setDeclareOpen(true)}
        >
          {t("planBlanc.declareCta")}
        </Button>
      )}

      <Dialog
        open={declareOpen}
        onClose={() => setDeclareOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: "error.main" }}>
          <CrisisAlertIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          {t("planBlanc.declareTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("planBlanc.declareBody")}
          </DialogContentText>
          <Box display="flex" flexDirection="column" gap={2}>
            {error && (
              <Alert severity="error">
                {t(`planBlanc.err.${error}`, { defaultValue: error })}
              </Alert>
            )}
            <FormControl size="small" fullWidth>
              <InputLabel>{t("planBlanc.severity")}</InputLabel>
              <Select
                label={t("planBlanc.severity")}
                value={severity}
                onChange={(e) => setSeverity(e.target.value as PlanBlancSeverity)}
              >
                <MenuItem value="ALPHA">{t("planBlanc.severity_label.ALPHA")}</MenuItem>
                <MenuItem value="BRAVO">{t("planBlanc.severity_label.BRAVO")}</MenuItem>
                <MenuItem value="CHARLIE">{t("planBlanc.severity_label.CHARLIE")}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t("planBlanc.reasonLabel")}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              minRows={2}
              maxRows={5}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              {t("planBlanc.declareHint")}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclareOpen(false)}>{t("common.cancel")}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => declareMutation.mutate()}
            disabled={declareMutation.isPending}
            startIcon={<CrisisAlertIcon />}
          >
            {t("planBlanc.confirmDeclare")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={endOpen}
        onClose={() => setEndOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("planBlanc.endTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("planBlanc.endBody")}
          </DialogContentText>
          <TextField
            label={t("planBlanc.endReasonLabel")}
            value={endReason}
            onChange={(e) => setEndReason(e.target.value)}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndOpen(false)}>{t("common.cancel")}</Button>
          <Button
            color="success"
            variant="contained"
            onClick={() => endMutation.mutate()}
            disabled={endMutation.isPending}
          >
            {t("planBlanc.confirmEnd")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
